/**
 * Reduces a book lookup to the words Google Books can actually match on.
 *
 * Catalogue titles carry editorial asides — "Vivarium (short story 'The New
 * Neighbours')", "Dune (Dune Chronicles #1)". Google Books treats every one of
 * those words as a term to satisfy, so the query is over-specified and comes
 * back with totalItems: 0. It is not an error, which is why this failed
 * quietly: the request succeeds, the book simply gets no cover. Measured on the
 * real query, the aside returns 0 results and the bare title returns 300.
 *
 * 25 of the 189 rows in sf_film_adaptations have a parenthetical title, and all
 * 25 are missing a poster or an ISBN.
 *
 * Deliberately NOT applied blindly — searchGoogleBooks tries the caller's query
 * first and only falls back to this when nothing matched, so a title where the
 * parenthetical genuinely helps is never second-guessed.
 */

/** Quote characters Google reads as phrase delimiters, straight and curly. */
const QUOTES = /["'‘’“”]/g;

export function simplifyBookQuery(query: string): string {
  if (!query) return '';

  let out = query;

  // Strip asides innermost-first so nested brackets collapse fully rather than
  // leaving orphaned punctuation behind.
  for (let i = 0; i < 4; i++) {
    const next = out
      .replace(/\([^()]*\)/g, ' ')
      .replace(/\[[^[\]]*\]/g, ' ');
    if (next === out) break;
    out = next;
  }

  out = out
    // Unbalanced brackets do occur in this catalogue; drop the strays too.
    .replace(/[()[\]]/g, ' ')
    .replace(QUOTES, ' ')
    // Google reads a leading - as "exclude this term", which turns a subtitle
    // dash into a filter that removes the very book being looked for.
    .replace(/\s-\s/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return out;
}

/**
 * True when simplifying would actually change the lookup, so callers can skip a
 * pointless second request for a query that has nothing to strip.
 */
export function isWorthSimplifying(query: string): boolean {
  const simplified = simplifyBookQuery(query);
  // A simplification down to almost nothing ("(1984)" -> "") would search for
  // the empty string and match everything, which is worse than no result.
  return simplified.length >= 3 && simplified !== query.trim();
}
